/*----------------------------------
- AGENDA PAGE
----------------------------------*/

/**
 * `/agenda`: the shared agenda board (ADR-0060) under the website header, at the board's own
 * 1500px measure, then the Wednesday newsletter band, the "Your dates" card, related links and
 * the GEO block at the page's 1240px one. Every part renders on the server; the board's map mounts
 * MapLibre only once it is on screen.
 *
 * @docs docs/features/agenda
 * @adr  ADR-0060
 * @rule `nowIso` (the request's clock) is the page's only clock: the board's `today`, the hrefs
 *       and the Reveal Day milestone all derive from it, so the server render and the hydration
 *       draw the same tree.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect } from 'preact/hooks';
import AgendaBoard from '@uniquedomains/components/agenda/board/AgendaBoard';
import {
    AGENDA_ICS_PATH,
    AGENDA_PATH,
    AGENDA_REVEAL_DAY,
    type PublicAgendaResponse,
} from '@uniquedomains/components/contracts/agenda/public';
import { formatTldLabel } from '@uniquedomains/core/domains/tlds';

import { AGENDA_NEWSLETTER_BAND_CONTENT, AGENDA_PAGE_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import useContext from '@/client/context';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildAgendaGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import NewsletterInkBand from '@/client/pages/landing/components/newsletter/NewsletterInkBand';
import { PAGE_CONTAINER_CLASS, WIDE_PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';
import { buildAgendaPageHref } from '@/common/libs/agenda/pageState';

import { fetchAgendaEventDetail } from '../lib/fetchAgendaEventDetail';
import { formatAgendaCountry } from '../lib/presentation';
import AgendaConversionSection from './components/AgendaConversionSection';
import AgendaFeedsDisclosure from './components/AgendaFeedsDisclosure';

import '../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type AgendaPageProps = {
    agenda: PublicAgendaResponse;
    navigation?: LandingHeaderDomainsNavigation | null;
    /** The request's clock (ISO), so the server render and the hydration read the same day. */
    nowIso: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'agenda';
const SUBSCRIBE_ANCHOR_ID = 'subscribe';

/** Reveal Day's chip and row open the applications page; the board decides when they show. */
const REVEAL_DAY = { ...AGENDA_REVEAL_DAY, href: AGENDA_REVEAL_DAY.path };

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function AgendaPage({ agenda, navigation, nowIso }: AgendaPageProps) {
    const context = useContext();
    const now = new Date(nowIso);
    const { filters } = agenda;
    const content = AGENDA_PAGE_CONTENT;
    const relatedLinks = getHumanPageRelationships(AGENDA_PATH).map((relationship) => ({
        label: relationship.label,
        href: relationship.targetPath,
    }));

    useEffect(() => {
        track('page_view', { page: TRACKING_PAGE, path: window.location.pathname + window.location.search });
    }, [filters.month, filters.category, filters.country, filters.tld]);

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation ?? EMPTY_LANDING_NAVIGATION}
                    currentPath={AGENDA_PATH}
                    trackingPage={TRACKING_PAGE}
                    mobileActions={{
                        secondary: {
                            label: content.mobileActions.feed,
                            href: AGENDA_ICS_PATH,
                            external: true,
                            testId: 'agenda-mobile-ics',
                        },
                        primary: {
                            label: AGENDA_NEWSLETTER_BAND_CONTENT.mobileAction,
                            href: `#${SUBSCRIBE_ANCHOR_ID}`,
                            testId: 'agenda-mobile-subscribe',
                        },
                    }}
                />

                <main id="top">
                    <div class={`${WIDE_PAGE_CONTAINER_CLASS} pt-7 lg:pt-12 [--agenda-board-chrome:218px] lg:[--agenda-board-chrome:170px]`}>
                        <AgendaBoard
                            titleVisibility="always"
                            testIdPrefix="agenda"
                            copy={content.board}
                            today={nowIso.slice(0, 10)}
                            filters={filters}
                            monthWindow={agenda.window}
                            entries={agenda.entries}
                            recentlyAdded={agenda.recentlyAdded}
                            undated={agenda.undated}
                            revealDay={REVEAL_DAY}
                            hrefs={{
                                month: (month) => buildAgendaPageHref({ ...filters, month }, now),
                                filters: (next) => buildAgendaPageHref(next, now),
                            }}
                            onNavigate={(href) => window.location.assign(href)}
                            onEntryClick={(entry) => track('agenda_event_click', { kind: entry.kind, id: entry.id, ...(entry.tld ? { tld: entry.tld } : {}) })}
                            loadEventDetail={fetchAgendaEventDetail}
                            onFilterApply={(filter, value) => track('agenda_filter_applied', { filter, value })}
                            onMapLoaded={({ points, places, durationMs }) => track('agenda_map_loaded', { points, places, durationMs })}
                            // With a fallback the handler logs without a toast: the map box already says it failed.
                            onMapError={(error) => context.app.handleError(error, content.board.map.failed)}
                            formatCountry={formatAgendaCountry}
                            formatTld={formatTldLabel}
                            titleActions={<AgendaFeedsDisclosure />}
                        />
                    </div>

                    <div class={`${PAGE_CONTAINER_CLASS} pt-14 lg:pt-20`}>
                        <NewsletterInkBand
                            anchorId={SUBSCRIBE_ANCHOR_ID}
                            source="website_agenda"
                            heading={AGENDA_NEWSLETTER_BAND_CONTENT.heading}
                            body={AGENDA_NEWSLETTER_BAND_CONTENT.body}
                        />
                    </div>

                    <div class={`${PAGE_CONTAINER_CLASS} pt-6 lg:pt-8`}>
                        <AgendaConversionSection />
                    </div>

                    <RelatedLinks links={relatedLinks} title={content.relatedLinksTitle} />

                    <PageGeoBlock className="pb-4" geo={buildAgendaGeoContext()} surface="agenda" />
                </main>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
            </div>
        </div>
    );
}
